import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getUsersDisplay } from '@/api/services/usersDisplayService';
import type { UserDisplay, UserState as KcUserState } from '@/api/types/UserDisplay';

import type { RootState } from './index';

// Length of the trailing UUID slice used as the FINAL fallback in the
// chained `selectAssignedUserName` selector — kept in sync with the
// dispatchersSlice fallback so an unknown sub renders identically
// regardless of which path through the chain was attempted last.
const UUID_FALLBACK_LEN = 8;

export type UsersStatus = 'idle' | 'loading' | 'ready' | 'error';

// Cache entry shape — keeps `state` (AKTIVNI / POZVANY / DEAKTIVOVANY) in
// addition to the displayName because future role-scoped pickers may want
// to grey out / annotate deactivated users (per ADR-0023 follow-up).
export interface UserCacheEntry {
  sub: string;
  displayName: string;
  state: KcUserState;
}

export interface UsersState {
  byId: Record<string, UserCacheEntry>;
  status: UsersStatus;
  fetchedAt: number | null;
}

const initialState: UsersState = {
  byId: {},
  status: 'idle',
  fetchedAt: null,
};

// Boot-time fetch dispatched from AppLayout in parallel with loadDispatchers.
// Failures degrade gracefully — `selectAssignedUserName` falls through to
// the `…last8` UUID tail; the axios interceptor surfaces the error toast.
export const loadUsers = createAsyncThunk<UserDisplay[]>(
  'users/load',
  async () => getUsersDisplay(),
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    // Marks the cache as stale without clearing entries — callers that mutate
    // the KC user set (admin user-management) should dispatch this so the
    // next `loadUsers()` refetches. Keeping `byId` populated avoids a flash
    // of `…last8` fallbacks while the refetch is in flight (mirror of
    // `invalidateDispatchers` rationale).
    invalidate(state) {
      state.status = 'idle';
      state.fetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadUsers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadUsers.fulfilled, (state, action) => {
        state.byId = Object.fromEntries(
          action.payload.map((u) => [
            u.sub,
            { sub: u.sub, displayName: u.displayName, state: u.state },
          ]),
        );
        state.status = 'ready';
        state.fetchedAt = Date.now();
      })
      .addCase(loadUsers.rejected, (state) => {
        state.status = 'error';
      });
  },
});

export const { invalidate: invalidateUsers } = usersSlice.actions;
export const usersReducer = usersSlice.reducer;

// ----- Selectors --------------------------------------------------------

// Generic resolver — returns '' for null, the displayName for a known sub,
// or '' for an unknown sub. Deliberately does NOT fall back to `…last8` —
// that's the chained `selectAssignedUserName`'s job. Use this when the
// caller wants to detect "unknown sub" explicitly (e.g. to hide an avatar).
export const selectUserName =
  (sub: string | null) =>
  (state: RootState): string => {
    if (sub === null) return '';
    return state.users.byId[sub]?.displayName ?? '';
  };

// Chained resolver — THE selector the 4 order surfaces consume. Priority:
//   1. dispatchers.byId — preserves [[dispatcher-display-name]] (ADR-0023)
//      semantics verbatim for dispatcher-role assignees.
//   2. users.byId — the generic completion; resolves any KC user (admin,
//      backoffice-only, multirole, deactivated) — the fix for the
//      "ne vsude funguje priprazeni dispecera" bug.
//   3. `…<last8>` UUID tail — final fallback when the sub references a KC
//      user that no longer exists (purged accounts, audit snapshots from a
//      since-deleted user).
//
// Lives in usersSlice (not dispatchersSlice) so consumers have a single
// import site — both slices' data is read internally and that's deliberate.
export const selectAssignedUserName =
  (sub: string | null) =>
  (state: RootState): string => {
    if (sub === null) return '';
    const dispatcherHit = state.dispatchers.byId[sub]?.displayName;
    if (dispatcherHit) return dispatcherHit;
    const userHit = state.users.byId[sub]?.displayName;
    if (userHit) return userHit;
    return `…${sub.slice(-UUID_FALLBACK_LEN)}`;
  };
